/** D50: iPads waiting for a Manager/Admin to approve them, shown on every office page. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { dateTime } from '../lib/format';
import { Button } from './ui';

export interface DeviceRequestRow {
  id: number;
  code: string;
  requesterName: string;
  deviceName: string;
  createdAt: string;
}

export function useDeviceRequests(enabled: boolean) {
  return useQuery({
    queryKey: ['device-requests'],
    queryFn: () => api.get<DeviceRequestRow[]>('/api/device-requests'),
    enabled,
    refetchInterval: 15_000,
  });
}

export function DeviceRequestsBanner() {
  const qc = useQueryClient();
  const rows = useDeviceRequests(true);
  const [error, setError] = useState<string | null>(null);
  if (!rows.data?.length) return null;

  const answer = async (r: DeviceRequestRow, approve: boolean) => {
    if (!approve && !confirm(`Turn down ${r.requesterName}'s iPad?`)) return;
    setError(null);
    try {
      await api.post(`/api/device-requests/${r.id}/${approve ? 'approve' : 'deny'}`);
    } catch (e) {
      setError(errorText(e));
    }
    void qc.invalidateQueries({ queryKey: ['device-requests'] });
    void qc.invalidateQueries({ queryKey: ['devices'] });
  };

  return (
    <div className="border-b border-amber-300 bg-amber-50">
      <div className="mx-auto max-w-[1600px] space-y-2 px-4 py-3">
        {error && <p className="text-sm text-red-700">{error}</p>}
        {rows.data.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 text-sm text-amber-950">
            <span>
              <b>{r.requesterName}</b> wants to set up an iPad (“{r.deviceName}”) at {dateTime(r.createdAt)}. Code on their screen:{' '}
              <b className="font-mono text-base">{r.code}</b>
            </span>
            <span className="ml-auto flex gap-2">
              <Button onClick={() => answer(r, true)}>Approve</Button>
              <Button variant="secondary" onClick={() => answer(r, false)}>Deny</Button>
            </span>
          </div>
        ))}
        <p className="text-xs text-amber-800">Approve only if the code matches the one on the mechanic's iPad.</p>
      </div>
    </div>
  );
}
